import type { Bi } from '@/sanskrit/lib/sanskrit/types'

/**
 * Sandhi engine. Vowel sandhi is computed by rule on a phoneme representation
 * (so no Devanagari cluster is ever cut apart); visarga and consonant sandhi
 * are taught through curated, verified examples.
 */

type Phoneme =
    | { t: 'C'; ch: string }
    | { t: 'V'; ch: string }
    | { t: 'X'; ch: string }

const CONSONANT_RE = /[क-हक़-य़]/
const VIRAMA = '्'

const MATRA_TO_VOWEL: Record<string, string> = {
    'ा': 'आ',
    'ि': 'इ',
    'ी': 'ई',
    'ु': 'उ',
    'ू': 'ऊ',
    'ृ': 'ऋ',
    'ॄ': 'ॠ',
    'ॢ': 'ऌ',
    'े': 'ए',
    'ै': 'ऐ',
    'ो': 'ओ',
    'ौ': 'औ'
}

const VOWEL_TO_MATRA: Record<string, string> = Object.fromEntries(
    [['अ', ''], ...Object.entries(MATRA_TO_VOWEL).map(([m, v]) => [v, m])]
)

const INDEPENDENT = new Set(['अ', 'आ', 'इ', 'ई', 'उ', 'ऊ', 'ऋ', 'ॠ', 'ऌ', 'ए', 'ऐ', 'ओ', 'औ'])

export const toPhonemes = (word: string): Phoneme[] => {
    const chars = Array.from(word.normalize('NFC'))
    const out: Phoneme[] = []

    for (let i = 0; i < chars.length; i++) {
        const ch = chars[i]

        if (CONSONANT_RE.test(ch)) {
            out.push({ t: 'C', ch })
            const next = chars[i + 1]

            if (next === VIRAMA) {
                i++
            }
            else if (next !== undefined && MATRA_TO_VOWEL[next]) {
                out.push({ t: 'V', ch: MATRA_TO_VOWEL[next] })
                i++
            }
            else {
                out.push({ t: 'V', ch: 'अ' })
            }

            continue
        }

        if (INDEPENDENT.has(ch)) {
            out.push({ t: 'V', ch })
            continue
        }

        out.push({ t: 'X', ch })
    }

    return out
}

export const fromPhonemes = (list: Phoneme[]): string => {
    let out = ''

    for (let i = 0; i < list.length; i++) {
        const p = list[i]

        if (p.t === 'C') {
            const next = list[i + 1]

            if (next && next.t === 'V') {
                out += p.ch + VOWEL_TO_MATRA[next.ch]
                i++
            }
            else {
                out += p.ch + VIRAMA
            }

            continue
        }

        out += p.ch
    }

    return out
}

export type VowelRule = 'savarna-dirgha' | 'guna' | 'vrddhi' | 'yan' | 'ayadi' | 'purvarupa'

export interface SandhiRuleInfo {
    id: VowelRule | 'visarga' | 'vyanjana'
    sa: string
    name: Bi
    sutra: string
    pattern: string
    text: Bi
}

export const VOWEL_RULES: Record<VowelRule, SandhiRuleInfo> = {
    'savarna-dirgha': {
        id: 'savarna-dirgha',
        sa: 'सवर्णदीर्घसन्धिः',
        name: { hi: 'सवर्णदीर्घ सन्धि', en: 'Savarṇa-dīrgha (like vowels lengthen)' },
        sutra: 'अकः सवर्णे दीर्घः (6.1.101)',
        pattern: 'अ/आ + अ/आ = आ · इ/ई + इ/ई = ई · उ/ऊ + उ/ऊ = ऊ',
        text: {
            hi: 'एक ही वर्ग के दो स्वर मिलकर उसी वर्ग का दीर्घ स्वर बन जाते हैं।',
            en: 'Two vowels of the same class merge into the long vowel of that class.'
        }
    },
    guna: {
        id: 'guna',
        sa: 'गुणसन्धिः',
        name: { hi: 'गुण सन्धि', en: 'Guṇa' },
        sutra: 'आद्गुणः (6.1.87)',
        pattern: 'अ/आ + इ/ई = ए · अ/आ + उ/ऊ = ओ · अ/आ + ऋ = अर्',
        text: {
            hi: 'अ या आ के बाद इ, उ, ऋ आए तो दोनों मिलकर गुण (ए, ओ, अर्) बनते हैं।',
            en: 'अ or आ followed by इ, उ or ऋ combine into their guṇa: ए, ओ, अर्.'
        }
    },
    vrddhi: {
        id: 'vrddhi',
        sa: 'वृद्धिसन्धिः',
        name: { hi: 'वृद्धि सन्धि', en: 'Vṛddhi' },
        sutra: 'वृद्धिरेचि (6.1.88)',
        pattern: 'अ/आ + ए/ऐ = ऐ · अ/आ + ओ/औ = औ',
        text: {
            hi: 'अ या आ के बाद ए/ऐ आए तो ऐ, और ओ/औ आए तो औ बनता है।',
            en: 'अ or आ followed by ए/ऐ gives ऐ, and followed by ओ/औ gives औ.'
        }
    },
    yan: {
        id: 'yan',
        sa: 'यण्सन्धिः',
        name: { hi: 'यण् सन्धि', en: 'Yaṇ (vowel becomes semivowel)' },
        sutra: 'इको यणचि (6.1.77)',
        pattern: 'इ/ई → य् · उ/ऊ → व् · ऋ → र् (भिन्न स्वर से पहले)',
        text: {
            hi: 'इ, उ, ऋ के बाद कोई भिन्न स्वर आए तो वे क्रमशः य्, व्, र् बन जाते हैं।',
            en: 'इ, उ, ऋ before a different vowel turn into the semivowels य्, व्, र्.'
        }
    },
    ayadi: {
        id: 'ayadi',
        sa: 'अयादिसन्धिः',
        name: { hi: 'अयादि सन्धि', en: 'Ayādi' },
        sutra: 'एचोऽयवायावः (6.1.78)',
        pattern: 'ए → अय् · ऐ → आय् · ओ → अव् · औ → आव्',
        text: {
            hi: 'ए, ऐ, ओ, औ के बाद स्वर आए तो वे अय्, आय्, अव्, आव् बन जाते हैं।',
            en: 'ए, ऐ, ओ, औ before a vowel become अय्, आय्, अव्, आव्.'
        }
    },
    purvarupa: {
        id: 'purvarupa',
        sa: 'पूर्वरूपसन्धिः',
        name: { hi: 'पूर्वरूप सन्धि', en: 'Pūrvarūpa' },
        sutra: 'एङः पदान्तादति (6.1.109)',
        pattern: 'पदान्त ए/ओ + अ = ए/ओ + ऽ',
        text: {
            hi: 'शब्द के अन्त के ए या ओ के बाद अ आए तो अ लुप्त हो जाता है और उसके स्थान पर ऽ (अवग्रह) लिखा जाता है।',
            en: 'After a word-final ए or ओ, a following अ disappears; its place is marked with ऽ (avagraha).'
        }
    }
}

export const VISARGA_RULE: SandhiRuleInfo = {
    id: 'visarga',
    sa: 'विसर्गसन्धिः',
    name: { hi: 'विसर्ग सन्धि', en: 'Visarga sandhi' },
    sutra: 'विसर्जनीयस्य सः (8.3.34) इत्यादि',
    pattern: 'ः + वर्ण',
    text: {
        hi: 'विसर्ग (ः) के बाद आने वाले वर्ण के अनुसार विसर्ग बदलता है: स्, श्, र्, ओ या लोप।',
        en: 'A visarga (ः) changes according to the following sound: into स्, श्, र्, ओ, or it disappears.'
    }
}

export const VYANJANA_RULE: SandhiRuleInfo = {
    id: 'vyanjana',
    sa: 'व्यञ्जनसन्धिः',
    name: { hi: 'व्यञ्जन सन्धि', en: 'Consonant sandhi' },
    sutra: 'झलां जशोऽन्ते (8.2.39) इत्यादि',
    pattern: 'व्यञ्जन + वर्ण',
    text: {
        hi: 'शब्द के अन्तिम व्यञ्जन में अगले वर्ण के प्रभाव से परिवर्तन।',
        en: 'A word-final consonant changes under the influence of the next sound.'
    }
}

const CLASS: Record<string, string> = {
    'अ': 'a', 'आ': 'a',
    'इ': 'i', 'ई': 'i',
    'उ': 'u', 'ऊ': 'u',
    'ऋ': 'r', 'ॠ': 'r'
}

const LONG: Record<string, string> = { a: 'आ', i: 'ई', u: 'ऊ', r: 'ॠ' }

export interface CombineResult {
    result: string
    rule: VowelRule
    left: string
    right: string
    junction: { from: string; to: string }
}

/**
 * Combine two words by vowel sandhi. `withinWord` selects अयादि (e.g. ने + अनम्)
 * instead of पूर्वरूप when ए/ओ meet अ.
 */
export const combineVowels = (w1: string, w2: string, withinWord = false): CombineResult | null => {
    const p1 = toPhonemes(w1)
    const p2 = toPhonemes(w2)
    const last = p1[p1.length - 1]
    const first = p2[0]

    if (!last || !first || last.t !== 'V' || first.t !== 'V') return null

    const v1 = last.ch
    const v2 = first.ch
    const head = p1.slice(0, -1)
    const tail = p2.slice(1)
    let middle: Phoneme[] = []
    let rule: VowelRule

    if (CLASS[v1] && CLASS[v1] === CLASS[v2]) {
        middle = [{ t: 'V', ch: LONG[CLASS[v1]] }]
        rule = 'savarna-dirgha'
    }
    else if (CLASS[v1] === 'a') {
        if (CLASS[v2] === 'i') middle = [{ t: 'V', ch: 'ए' }]
        else if (CLASS[v2] === 'u') middle = [{ t: 'V', ch: 'ओ' }]
        else if (CLASS[v2] === 'r') middle = [{ t: 'V', ch: 'अ' }, { t: 'C', ch: 'र' }]
        else if (v2 === 'ए' || v2 === 'ऐ') middle = [{ t: 'V', ch: 'ऐ' }]
        else if (v2 === 'ओ' || v2 === 'औ') middle = [{ t: 'V', ch: 'औ' }]
        else return null

        rule = v2 === 'ए' || v2 === 'ऐ' || v2 === 'ओ' || v2 === 'औ' ? 'vrddhi' : 'guna'
    }
    else if (CLASS[v1] === 'i' || CLASS[v1] === 'u' || CLASS[v1] === 'r') {
        const semi = CLASS[v1] === 'i' ? 'य' : CLASS[v1] === 'u' ? 'व' : 'र'
        middle = [{ t: 'C', ch: semi }, { t: 'V', ch: v2 }]
        rule = 'yan'
    }
    else if ((v1 === 'ए' || v1 === 'ओ') && v2 === 'अ' && !withinWord) {
        middle = [{ t: 'V', ch: v1 }, { t: 'X', ch: 'ऽ' }]
        rule = 'purvarupa'
    }
    else if (v1 === 'ए' || v1 === 'ऐ' || v1 === 'ओ' || v1 === 'औ') {
        const vowel = v1 === 'ए' || v1 === 'ओ' ? 'अ' : 'आ'
        const semi = v1 === 'ए' || v1 === 'ऐ' ? 'य' : 'व'
        middle = [{ t: 'V', ch: vowel }, { t: 'C', ch: semi }, { t: 'V', ch: v2 }]
        rule = 'ayadi'
    }
    else {
        return null
    }

    const junctionTo = fromPhonemes(middle.length === 1 && middle[0].t === 'V' ? middle : middle)

    return {
        result: fromPhonemes([...head, ...middle, ...tail]),
        rule,
        left: w1,
        right: w2,
        junction: { from: `${v1} + ${v2}`, to: junctionTo }
    }
}

export interface SandhiExample {
    id: string
    left: string
    right: string
    result: string
    kind: VowelRule | 'visarga' | 'vyanjana'
    withinWord?: boolean
    meaning: Bi
    steps: Bi
    sutra?: string
}

const v = (id: string, left: string, right: string, result: string, kind: VowelRule, hi: string, en: string, withinWord = false): SandhiExample => ({
    id,
    left,
    right,
    result,
    kind,
    withinWord,
    meaning: { hi, en },
    steps: VOWEL_RULES[kind].text,
    sutra: VOWEL_RULES[kind].sutra
})

export const VOWEL_EXAMPLES: SandhiExample[] = [
    v('vidyalaya', 'विद्या', 'आलयः', 'विद्यालयः', 'savarna-dirgha', 'विद्या का घर, विद्यालय', 'school (abode of learning)'),
    v('himalaya', 'हिम', 'आलयः', 'हिमालयः', 'savarna-dirgha', 'हिम का घर', 'Himālaya (abode of snow)'),
    v('kavindra', 'कवि', 'इन्द्रः', 'कवीन्द्रः', 'savarna-dirgha', 'कवियों में श्रेष्ठ', 'king of poets'),
    v('bhanudaya', 'भानु', 'उदयः', 'भानूदयः', 'savarna-dirgha', 'सूर्य का उदय', 'sunrise'),
    v('ganesa', 'गण', 'ईशः', 'गणेशः', 'guna', 'गणों के स्वामी', 'lord of the gaṇas (Gaṇeśa)'),
    v('mahendra', 'महा', 'इन्द्रः', 'महेन्द्रः', 'guna', 'महान् इन्द्र', 'great Indra'),
    v('suryodaya', 'सूर्य', 'उदयः', 'सूर्योदयः', 'guna', 'सूर्य का उदय', 'sunrise'),
    v('mahotsava', 'महा', 'उत्सवः', 'महोत्सवः', 'guna', 'बड़ा उत्सव', 'great festival'),
    v('maharsi', 'महा', 'ऋषिः', 'महर्षिः', 'guna', 'महान् ऋषि', 'great sage'),
    v('devarsi', 'देव', 'ऋषिः', 'देवर्षिः', 'guna', 'देवताओं में ऋषि', 'divine sage'),
    v('ekaikam', 'एक', 'एकम्', 'एकैकम्', 'vrddhi', 'एक-एक करके', 'one by one'),
    v('sadaiva', 'सदा', 'एव', 'सदैव', 'vrddhi', 'हमेशा ही', 'always indeed'),
    v('vanausadhi', 'वन', 'ओषधिः', 'वनौषधिः', 'vrddhi', 'वन की औषधि', 'forest herb'),
    v('mahausadha', 'महा', 'औषधम्', 'महौषधम्', 'vrddhi', 'बड़ी औषधि', 'great medicine'),
    v('ityadi', 'इति', 'आदि', 'इत्यादि', 'yan', 'इत्यादि, वगैरह', 'and so on'),
    v('yadyapi', 'यदि', 'अपि', 'यद्यपि', 'yan', 'यद्यपि, हालाँकि', 'although'),
    v('svagatam', 'सु', 'आगतम्', 'स्वागतम्', 'yan', 'स्वागत', 'welcome'),
    v('anvaya', 'अनु', 'अयः', 'अन्वयः', 'yan', 'अन्वय, क्रम', 'connection, prose order'),
    v('pitrajna', 'पितृ', 'आज्ञा', 'पित्राज्ञा', 'yan', 'पिता की आज्ञा', 'father’s command'),
    v('nayana', 'ने', 'अनम्', 'नयनम्', 'ayadi', 'आँख', 'eye', true),
    v('nayaka', 'नै', 'अकः', 'नायकः', 'ayadi', 'नायक', 'leader, hero', true),
    v('pavana', 'पो', 'अनः', 'पवनः', 'ayadi', 'हवा', 'wind', true),
    v('pavaka', 'पौ', 'अकः', 'पावकः', 'ayadi', 'अग्नि', 'fire', true),
    v('vane-smin', 'वने', 'अस्मिन्', 'वनेऽस्मिन्', 'purvarupa', 'इस वन में', 'in this forest'),
    v('hare-va', 'हरे', 'अव', 'हरेऽव', 'purvarupa', 'हे हरि, रक्षा करो', 'O Hari, protect')
]

const x = (id: string, left: string, right: string, result: string, kind: 'visarga' | 'vyanjana', hi: string, en: string, stepsHi: string, stepsEn: string, sutra: string): SandhiExample => ({
    id,
    left,
    right,
    result,
    kind,
    meaning: { hi, en },
    steps: { hi: stepsHi, en: stepsEn },
    sutra
})

export const VISARGA_EXAMPLES: SandhiExample[] = [
    x('namaste', 'नमः', 'ते', 'नमस्ते', 'visarga', 'तुम्हें नमस्कार', 'salutation to you', 'त/थ से पहले विसर्ग का स् हो जाता है।', 'Before त or थ the visarga becomes स्.', 'विसर्जनीयस्य सः (8.3.34)'),
    x('ramasca', 'रामः', 'च', 'रामश्च', 'visarga', 'और राम', 'and Rāma', 'च/छ से पहले विसर्ग का स्, फिर स् का श् हो जाता है।', 'Before च or छ the visarga becomes स्, which then becomes श्.', 'विसर्जनीयस्य सः (8.3.34), स्तोः श्चुना श्चुः (8.4.40)'),
    x('ramo-pi', 'रामः', 'अपि', 'रामोऽपि', 'visarga', 'राम भी', 'Rāma too', 'अ के बाद का विसर्ग, अगले अ से पहले ओ हो जाता है, और अगला अ लुप्त होकर ऽ बनता है।', 'An अः before अ becomes ओ, and the following अ disappears into ऽ.', 'अतो रोरप्लुतादप्लुते (6.1.113), एङः पदान्तादति (6.1.109)'),
    x('balako-gacchati', 'बालकः', 'गच्छति', 'बालको गच्छति', 'visarga', 'बालक जाता है', 'the boy goes', 'अः के बाद कोई कोमल (घोष) व्यञ्जन आए तो अः का ओ हो जाता है।', 'An अः before a voiced consonant becomes ओ.', 'हशि च (6.1.114)'),
    x('harirapi', 'हरिः', 'अपि', 'हरिरपि', 'visarga', 'हरि भी', 'Hari too', 'अ/आ के अतिरिक्त किसी स्वर के बाद का विसर्ग, स्वर या घोष वर्ण से पहले र् बन जाता है।', 'A visarga after any vowel other than अ/आ becomes र् before a vowel or voiced sound.', 'ससजुषो रुः (8.2.66)'),
    x('gururvadati', 'गुरुः', 'वदति', 'गुरुर्वदति', 'visarga', 'गुरु बोलते हैं', 'the teacher speaks', 'उ के बाद का विसर्ग, घोष व्यञ्जन से पहले र् बनता है।', 'After उ, the visarga becomes र् before a voiced consonant.', 'ससजुषो रुः (8.2.66)'),
    x('balaka-gacchanti', 'बालकाः', 'गच्छन्ति', 'बालका गच्छन्ति', 'visarga', 'बालक जाते हैं', 'the boys go', 'आः के बाद घोष वर्ण हो तो विसर्ग लुप्त हो जाता है।', 'An आः before a voiced sound loses its visarga.', 'भोभगोअघोअपूर्वस्य योऽशि (8.3.17), हलि सर्वेषाम् (8.3.22)'),
    x('sa-pathati', 'सः', 'पठति', 'स पठति', 'visarga', 'वह पढ़ता है', 'he reads', 'सः और एषः का विसर्ग व्यञ्जन से पहले लुप्त हो जाता है। पाठ्यपुस्तकें शुरुआत में सन्धि के बिना "सः पठति" लिखती हैं; प्राचीन ग्रन्थों में "स पठति" मिलेगा।', 'The visarga of सः and एषः drops before a consonant. Beginner books write "सः पठति" without sandhi; classical texts write "स पठति".', 'एतत्तदोः सुलोपोऽकोरनञ्समासे हलि (6.1.132)'),
    x('punarapi', 'पुनः', 'अपि', 'पुनरपि', 'visarga', 'फिर से भी', 'again too', 'पुनः का विसर्ग मूल रूप से र् है, इसलिए स्वर से पहले र् लौट आता है।', 'The visarga of पुनः comes from an original र्, which returns before a vowel.', 'पुनर् (रेफान्त अव्यय)'),
    x('niroga', 'निः', 'रोगः', 'नीरोगः', 'visarga', 'रोग से रहित', 'free of disease', 'र् से पहले का र्/विसर्ग लुप्त होता है और उससे पहले का स्वर दीर्घ हो जाता है।', 'Before र्, the preceding र्/visarga drops and the vowel before it lengthens.', 'रो रि (8.3.14), ढ्रलोपे पूर्वस्य दीर्घोऽणः (6.3.111)')
]

export const CONSONANT_EXAMPLES: SandhiExample[] = [
    x('vagisa', 'वाक्', 'ईशः', 'वागीशः', 'vyanjana', 'वाणी के स्वामी', 'lord of speech', 'शब्द के अन्त का कठोर व्यञ्जन (क्) स्वर से पहले अपने वर्ग का तीसरा अक्षर (ग्) बन जाता है।', 'A final hard consonant (क्) becomes the third letter of its row (ग्) before a vowel.', 'झलां जशोऽन्ते (8.2.39)'),
    x('sadacara', 'सत्', 'आचारः', 'सदाचारः', 'vyanjana', 'अच्छा आचरण', 'good conduct', 'त् → द् स्वर से पहले।', 'त् → द् before a vowel.', 'झलां जशोऽन्ते (8.2.39)'),
    x('diggaja', 'दिक्', 'गजः', 'दिग्गजः', 'vyanjana', 'दिशा का हाथी, महान् व्यक्ति', 'elephant of the quarters; a giant', 'क् → ग् घोष व्यञ्जन से पहले।', 'क् → ग् before a voiced consonant.', 'झलां जशोऽन्ते (8.2.39)'),
    x('sadanana', 'षट्', 'आननः', 'षडाननः', 'vyanjana', 'छह मुख वाले (कार्तिकेय)', 'six-faced (Kārttikeya)', 'ट् → ड् स्वर से पहले।', 'ट् → ड् before a vowel.', 'झलां जशोऽन्ते (8.2.39)'),
    x('jagannatha', 'जगत्', 'नाथः', 'जगन्नाथः', 'vyanjana', 'संसार के स्वामी', 'lord of the world', 'नासिक्य (न, म) से पहले व्यञ्जन अपने वर्ग का नासिक्य बन जाता है: त् → न्।', 'Before a nasal, a consonant becomes the nasal of its row: त् → न्.', 'यरोऽनुनासिकेऽनुनासिको वा (8.4.45)'),
    x('vanmaya', 'वाक्', 'मयम्', 'वाङ्मयम्', 'vyanjana', 'साहित्य', 'literature', 'क् → ङ् नासिक्य म् से पहले।', 'क् → ङ् before the nasal म्.', 'यरोऽनुनासिकेऽनुनासिको वा (8.4.45)'),
    x('tallina', 'तत्', 'लीनः', 'तल्लीनः', 'vyanjana', 'उसमें लीन', 'absorbed in that', 'त् से परे ल् हो तो त् का ल् हो जाता है।', 'त् before ल् becomes ल्.', 'तोर्लि (8.4.60)'),
    x('saccit', 'सत्', 'चित्', 'सच्चित्', 'vyanjana', 'सत् और चित्', 'being and consciousness', 'त् से परे च् हो तो त् का च् हो जाता है।', 'त् before च् becomes च्.', 'स्तोः श्चुना श्चुः (8.4.40)'),
    x('uddhara', 'उत्', 'हारः', 'उद्धारः', 'vyanjana', 'उद्धार, बचाव', 'rescue, uplift', 'त् → द्, और उसके बाद का ह् → ध्।', 'त् → द्, and the following ह् → ध्.', 'झयो होऽन्यतरस्याम् (8.4.62)'),
    x('sankalpa', 'सम्', 'कल्पः', 'सङ्कल्पः', 'vyanjana', 'संकल्प', 'resolve', 'म् व्यञ्जन से पहले अनुस्वार (संकल्पः) या वर्ग का नासिक्य (सङ्कल्पः) बनता है।', 'म् before a consonant becomes anusvāra (संकल्पः) or the nasal of that row (सङ्कल्पः).', 'मोऽनुस्वारः (8.3.23), वा पदान्तस्य (8.4.59)')
]

export const ALL_SANDHI_EXAMPLES: SandhiExample[] = [...VOWEL_EXAMPLES, ...VISARGA_EXAMPLES, ...CONSONANT_EXAMPLES]

/** Words the reverse analyser accepts as real components. */
export const SANDHI_LEXICON = new Set<string>(ALL_SANDHI_EXAMPLES.flatMap(e => [e.left, e.right]))

export interface SplitCandidate {
    left: string
    right: string
    rule: VowelRule
    known: boolean
}

const longOf: Record<string, string[]> = {
    'आ': ['अ', 'आ'],
    'ई': ['इ', 'ई'],
    'ऊ': ['उ', 'ऊ']
}

/**
 * Reverse analysis (सन्धि-विच्छेद) for vowel sandhi: every way the vowel at
 * phoneme index `index` could have arisen, with the parts checked against the lexicon.
 */
export const splitAt = (word: string, index: number, lexicon: Set<string> = SANDHI_LEXICON): SplitCandidate[] => {
    const p = toPhonemes(word)
    const at = p[index]
    if (!at || index === 0) return []

    const out: SplitCandidate[] = []
    const head = p.slice(0, index)
    const tail = p.slice(index + 1)

    const push = (leftEnd: Phoneme[], rightStart: Phoneme[], rule: VowelRule, headOverride?: Phoneme[]) => {
        const left = fromPhonemes([...(headOverride ?? head), ...leftEnd])
        const right = fromPhonemes([...rightStart, ...tail])
        out.push({ left, right, rule, known: lexicon.has(left) && lexicon.has(right) })
    }

    if (at.t === 'V') {
        if (longOf[at.ch]) {
            for (const a of longOf[at.ch]) for (const b of longOf[at.ch]) push([{ t: 'V', ch: a }], [{ t: 'V', ch: b }], 'savarna-dirgha')
        }

        const guna: Record<string, string[]> = { 'ए': ['इ', 'ई'], 'ओ': ['उ', 'ऊ'] }
        const vrddhi: Record<string, string[]> = { 'ऐ': ['ए', 'ऐ'], 'औ': ['ओ', 'औ'] }

        if (guna[at.ch]) {
            for (const a of ['अ', 'आ']) for (const b of guna[at.ch]) push([{ t: 'V', ch: a }], [{ t: 'V', ch: b }], 'guna')
        }

        if (vrddhi[at.ch]) {
            for (const a of ['अ', 'आ']) for (const b of vrddhi[at.ch]) push([{ t: 'V', ch: a }], [{ t: 'V', ch: b }], 'vrddhi')
        }

        const next = p[index + 1]
        const afterNext = p[index + 2]

        if (at.ch === 'अ' && next?.t === 'C' && next.ch === 'र' && afterNext?.t === 'C') {
            for (const a of ['अ', 'आ']) {
                out.push({
                    left: fromPhonemes([...head, { t: 'V', ch: a }]),
                    right: fromPhonemes([{ t: 'V', ch: 'ऋ' }, ...p.slice(index + 2)]),
                    rule: 'guna',
                    known: false
                })
            }
        }

        const prev = p[index - 1]
        const beforePrev = p[index - 2]

        if (prev?.t === 'C' && beforePrev?.t === 'C' && (prev.ch === 'य' || prev.ch === 'व' || prev.ch === 'र')) {
            const sources = prev.ch === 'य' ? ['इ', 'ई'] : prev.ch === 'व' ? ['उ', 'ऊ'] : ['ऋ']
            const shortHead = p.slice(0, index - 1)

            for (const s of sources) push([{ t: 'V', ch: s }], [at], 'yan', shortHead)
        }
    }

    for (const c of out) c.known = lexicon.has(c.left) && lexicon.has(c.right)

    const seen = new Set<string>()

    return out
        .filter(c => {
            const key = `${c.left}+${c.right}`
            if (seen.has(key)) return false
            seen.add(key)
            return true
        })
        .sort((a, b) => Number(b.known) - Number(a.known))
}

/** Phoneme indexes of vowels where a split could be tried (used to make the word tappable). */
export const splittableIndexes = (word: string): number[] => {
    return toPhonemes(word)
        .map((p, i) => ({ p, i }))
        .filter(({ p, i }) => i > 0 && p.t === 'V')
        .map(({ i }) => i)
}

export const phonemeLabel = (word: string, index: number): string => {
    const p = toPhonemes(word)
    const ph = p[index]
    if (!ph) return ''
    if (ph.t !== 'V') return ph.ch
    const prev = p[index - 1]
    return prev && prev.t === 'C' ? prev.ch + VOWEL_TO_MATRA[ph.ch] : ph.ch
}
